import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Switch,
  DialogContentText,
  LinearProgress
} from '@material-ui/core';
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import { Alert } from '@material-ui/lab';
import {
  Send,
  History,
  Delete,
  Visibility,
  Message
} from '@material-ui/icons';
import { API_POST } from '../../services/api.service';
import { getTemplates, getWhatsappUserDetails } from '../common/whatsapp';
import { 
  processPhoneNumbersForBroadcast,
  createBroadcastPayload,
  processTemplateVariables,
  extractTemplateVariables,
} from '../common/metaWhatsAppAPI';

const Broadcast = ({ user, phoneSelections }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Broadcast form state
  const [broadcastForm, setBroadcastForm] = useState({
    broadcastName: '',
    selectedPhone: '',
    selectedTemplate: '',
    phoneNumbers: '',
    variables: {},
    scheduledTime: null,
    isScheduled: false
  });

  // Template and broadcast data
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [previewData, setPreviewData] = useState(null);

  // Pagination for broadcast list
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [previewDialog, setPreviewDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [viewDetailsDialog, setViewDetailsDialog] = useState({ open: false, broadcastId: null });
  const [broadcastLogs, setBroadcastLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (user && user.admin) {
      fetchTemplates();
      fetchBroadcasts();
    }
  }, [user]);

  const fetchTemplates = async (phonenumber) => {
    try {
      const response = await getTemplates({ 
        admin: user.admin, 
        username: user.username, 
        ...(phonenumber ? { phoneNumberId: phonenumber } : { allTemplates: true })
      });
      setTemplates(response || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showSnackbar('Error fetching templates', 'error');
    }
  };

  const fetchBroadcasts = async () => {
    try {
      const response = await API_POST('action/getdata', {
        root: 'whatsappBroadcasts',
        con: { admin: user.admin },
        cols: {}
      });

      if (response && response.status === 200) {
        setBroadcasts(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
      showSnackbar('Error fetching broadcasts', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    if(name === 'selectedPhone'){
      await fetchTemplates(value);
    }
    setBroadcastForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setBroadcastForm(prev => ({ ...prev, selectedTemplate: templateId }));
    
    // Find and set template data
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplateData(template);
    
    // Initialize variables for this template
    if (template && template.body && template.body.text) {
      const variables = extractVariables(template.body.text);
      const initialVariables = {};
      variables.forEach(variable => {
        initialVariables[variable] = '';
      });
      setBroadcastForm(prev => ({ ...prev, variables: initialVariables }));
    }
  };

  const extractVariables = extractTemplateVariables;

  const handleVariableChange = (variable, value) => {
    setBroadcastForm(prev => ({
      ...prev,
      variables: { ...prev.variables, [variable]: value }
    }));
  };

  const parsePhoneNumbers = (phoneText) => {
    const phoneNumbers = phoneText
      .split('\n')
      .map(phone => phone.trim())
      .filter(phone => phone.length > 0);
    
    const processed = processPhoneNumbersForBroadcast(phoneNumbers);
    return processed.valid.map(phone => phone.normalized);
  };

  const generatePreview = () => {
    if (!selectedTemplateData) return;

    console.log(selectedTemplateData);
    const phoneNumbers = parsePhoneNumbers(broadcastForm.phoneNumbers);
    const preview = {
      template: selectedTemplateData,
      phoneNumbers: phoneNumbers.slice(0, 5), // Show first 5 for preview
      totalRecipients: phoneNumbers.length,
      variables: broadcastForm.variables,
      processedText: processTemplateText(selectedTemplateData.componenet[0].text, broadcastForm.variables)
    };

    setPreviewData(preview);
    setPreviewDialog(true);
  };

  const processTemplateText = processTemplateVariables;

  const handleSubmit = async () => {
    if (!broadcastForm.broadcastName || !broadcastForm.selectedTemplate || !broadcastForm.phoneNumbers) {
      showSnackbar('Please fill all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      // Create broadcast payload using utility function
      const broadcastData = createBroadcastPayload(broadcastForm, selectedTemplateData, user);
      
      // Add additional fields
      const fullBroadcastData = {
        ...broadcastData,
        sentCount: 0,
        failedCount: 0
      };

      // Save broadcast to database
      const response = await API_POST('action/addRecord', {
        root: 'whatsappBroadcasts',
        body: fullBroadcastData
      });

      if (response && response.status === 200) {
        // Add phone numbers to queue for processing
        await addPhoneNumbersToQueue(broadcastData, response.data);
        
        showSnackbar('Broadcast created successfully! Processing will begin shortly.', 'success');
        resetForm();
        fetchBroadcasts();
      } else {
        showSnackbar('Error creating broadcast', 'error');
      }
    } catch (error) {
      console.error('Error creating broadcast:', error);
      showSnackbar('Error creating broadcast', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addPhoneNumbersToQueue = async (broadcastData, broadcastId) => {
    try {
        const WhtasappUser = await getWhatsappUserDetails();
      // Create queue items for each phone number
      const queueItems = broadcastData.phoneNumbers.map((phoneData, index) => ({
        type: 'whatsappBroadcast',
        selectedTemplateData: selectedTemplateData,
        whatsappUserDetails: WhtasappUser,
        broadcastId: broadcastId,
        phoneNumber: phoneData.normalized,
        formattedPhone: phoneData.formatted,
        templateId: broadcastData.templateId,
        templateName: broadcastData.templateName,
        variables: broadcastData.variables,
        selectedPhone: broadcastData.selectedPhone,
        admin: broadcastData.admin,
        createdBy: broadcastData.createdBy,
        priority: index, // Process in order
        createdOn: new Date(),
        status: 'pending',
        retryCount: 0
      }));

      // Add all queue items to the _queue collection (MessageQueue system)
      for (const queueItem of queueItems) {
        await API_POST('action/addRecord', {
          root: 'whatsappBroadcastQueue',
          body: queueItem
        });
      }

      console.log(`Added ${queueItems.length} phone numbers to MessageQueue for broadcast ${broadcastId}`);
    } catch (error) {
      console.error('Error adding phone numbers to queue:', error);
      throw error;
    }
  };

  const resetForm = () => {
    setBroadcastForm({
      broadcastName: '',
      selectedPhone: '',
      selectedTemplate: '',
      phoneNumbers: '',
      variables: {},
      scheduledTime: null,
      isScheduled: false
    });
    setSelectedTemplateData(null);
  };

  const handleDeleteBroadcast = async (broadcastId) => {
    try {
      await API_POST('action/update', {
        root: 'whatsappBroadcasts',
        querydata: { _id: broadcastId },
        body: { isActive: false }
      });
      
      showSnackbar('Broadcast deleted successfully', 'success');
      fetchBroadcasts();
    } catch (error) {
      console.error('Error deleting broadcast:', error);
      showSnackbar('Error deleting broadcast', 'error');
    }
    setDeleteDialog({ open: false, id: null });
  };

  const handleViewDetails = async (broadcastId) => {
    setViewDetailsDialog({ open: true, broadcastId });
    setLogsLoading(true);
    
    try{
      const response = await API_POST('action/getdata', {
        root: 'whatsappBroadcastLogs',
        con: { admin: user.admin,broadcastId: broadcastId },
        cols: {}
      });

      if (response && response.status === 200) {
        setBroadcastLogs(response.data || []);
      } else {
        showSnackbar('Error fetching broadcast details', 'error');
      }
    } catch (error) {
      console.error('Error fetching broadcast details:', error);
      showSnackbar('Error fetching broadcast details', 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const renderBroadcastForm = () => (
    <Grid container spacing={3}>
      {/* Left Side - Preview Section */}
      <Grid item xs={12} lg={5}>
        <Card style={{ height: '100%', position: 'sticky', top: 20 }}>
          <CardHeader>
            <Typography variant="h6" style={{ color: '#1976d2', fontWeight: 600 }}>
              📱 Message Preview
            </Typography>
          </CardHeader>
          <CardBody>
            {previewData ? (
              <Box>
                <Paper 
                  elevation={2} 
                  style={{ 
                    padding: 20, 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: 12,
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Template: {previewData.templateName}
                  </Typography>
                  <Divider style={{ margin: '10px 0' }} />
                  <Typography variant="body1" style={{ lineHeight: 1.6 }}>
                    {previewData.processedMessage}
                  </Typography>
                </Paper>
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    Recipients: {previewData && previewData.phoneNumbers.length || 0} numbers
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                minHeight={200}
                textAlign="center"
              >
                <Message style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                <Typography variant="body2" color="textSecondary">
                  Fill in the form to see message preview
                </Typography>
              </Box>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Right Side - Form Section */}
      <Grid item xs={12} lg={7}>
        <Card>
          <CardHeader>
            <Typography variant="h5" style={{ color: '#1976d2', fontWeight: 600 }}>
              🚀 Create New Broadcast
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Send WhatsApp messages to multiple recipients using templates
            </Typography>
          </CardHeader>
          <CardBody>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Broadcast Name *"
                  name="broadcastName"
                  value={broadcastForm.broadcastName}
                  onChange={handleInputChange}
                  variant="outlined"
                  helperText="Give your broadcast a descriptive name"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Select Phone Number *</InputLabel>
                  <Select
                    name="selectedPhone"
                    value={broadcastForm.selectedPhone}
                    onChange={handleInputChange}
                    label="Select Phone Number *"
                  >
                    {phoneSelections.map((phone) => (
                      <MenuItem key={phone.key} value={phone.key}>
                        {phone.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Select Template *</InputLabel>
                  <Select
                    name="selectedTemplate"
                    value={broadcastForm.selectedTemplate}
                    onChange={handleTemplateChange}
                    label="Select Template *"
                  >
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name} - {template.status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {selectedTemplateData && Object.keys(broadcastForm.variables).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom style={{ color: '#1976d2' }}>
                    📝 Template Variables
                  </Typography>
                  <Paper elevation={1} style={{ padding: 16, backgroundColor: '#f8f9fa' }}>
                    <Grid container spacing={2}>
                      {Object.keys(broadcastForm.variables).map((variable) => (
                        <Grid item xs={12} md={6} key={variable}>
                          <TextField
                            fullWidth
                            label={variable}
                            value={broadcastForm.variables[variable]}
                            onChange={(e) => handleVariableChange(variable, e.target.value)}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Phone Numbers (one per line) *"
                  name="phoneNumbers"
                  value={broadcastForm.phoneNumbers}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Enter phone numbers, one per line:&#10;9876543210&#10;+919876543211&#10;919876543212"
                  helperText="Enter phone numbers in any format (+91, 91, 0, or 10-digit)"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={generatePreview}
                    disabled={!selectedTemplateData || !broadcastForm.phoneNumbers}
                    startIcon={<Visibility />}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Send />}
                    onClick={handleSubmit}
                    disabled={loading}
                    size="large"
                  >
                    {loading ? <CircularProgress size={20} /> : 'Send Broadcast'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardBody>
        </Card>
      </Grid>
    </Grid>
  );

  const renderBroadcastList = () => (
    <Card>
      <CardHeader>
        <Typography variant="h5" style={{ color: '#1976d2', fontWeight: 600 }}>
          📊 Broadcast History
        </Typography>
        <Typography variant="body2" color="textSecondary">
          View and manage your broadcast campaigns
        </Typography>
      </CardHeader>
      <CardBody>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                <TableCell style={{ fontWeight: 600 }}>Broadcast Name</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Template</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Recipients</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {broadcasts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((broadcast) => (
                  <TableRow key={broadcast._id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
                        {broadcast.broadcastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {templates && templates.find(t => t.id === broadcast.templateId) && 
                         templates.find(t => t.id === broadcast.templateId).name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {broadcast.sentCount || 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          / {broadcast.totalRecipients}
                        </Typography>
                        <Box 
                          width={60} 
                          height={4} 
                          bgcolor="#e0e0e0" 
                          borderRadius={2}
                          position="relative"
                        >
                          <Box
                            width={`${((broadcast.sentCount || 0) / broadcast.totalRecipients) * 100}%`}
                            height="100%"
                            bgcolor="#4caf50"
                            borderRadius={2}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={broadcast.status}
                        color={getStatusColor(broadcast.status)}
                        size="small"
                        style={{ 
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(broadcast.createdOn)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small"
                            onClick={() => handleViewDetails(broadcast._id)}
                            style={{ color: '#1976d2' }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={broadcasts.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(e.target.value)}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </CardBody>
    </Card>
  );

  return (
    <Box>
      <Box mb={3}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab 
            label="Create Broadcast" 
            icon={<Send />} 
            style={{ 
              textTransform: 'none', 
              fontSize: '16px',
              fontWeight: 600
            }}
          />
          <Tab 
            label="Broadcast History" 
            icon={<History />} 
            style={{ 
              textTransform: 'none', 
              fontSize: '16px',
              fontWeight: 600
            }}
          />
        </Tabs>
      </Box>

      <Box>
        {activeTab === 0 && renderBroadcastForm()}
        {activeTab === 1 && renderBroadcastList()}
      </Box>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Broadcast Preview</DialogTitle>
        <DialogContent>
          {previewData && (
            <Box>
              <Typography variant="h6" gutterBottom>Template Preview:</Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {previewData.processedText}
                </Typography>
              </Paper>
              
              <Typography variant="h6" gutterBottom>Recipients ({previewData.totalRecipients}):</Typography>
              <List dense>
                {previewData.phoneNumbers.map((phone, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={phone} />
                  </ListItem>
                ))}
                {previewData.totalRecipients > 5 && (
                  <ListItem>
                    <ListItemText primary={`... and ${previewData.totalRecipients - 5} more`} />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this broadcast?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button 
            onClick={() => handleDeleteBroadcast(deleteDialog.id)} 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDetailsDialog.open} 
        onClose={() => setViewDetailsDialog({ open: false, broadcastId: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" style={{ color: '#1976d2', fontWeight: 600 }}>
            📱 Broadcast Details
          </Typography>
          <Typography variant="body2" color="textSecondary">
            View individual message delivery status
          </Typography>
        </DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" p={4}>
              <CircularProgress size={40} />
              <Typography variant="body1" style={{ marginTop: 16, fontWeight: 500 }}>
                Loading broadcast details...
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  Message Delivery Status
                </Typography>
                <Chip 
                  label={`${broadcastLogs.length} messages`} 
                  color="primary" 
                  variant="outlined"
                />
              </Box>
              
              <TableContainer component={Paper} style={{ maxHeight: 500, borderRadius: 8 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell style={{ fontWeight: 600 }}>Phone Number</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Template</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Created</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {broadcastLogs.map((log, index) => (
                      <TableRow key={log._id || index} hover>
                        <TableCell>
                          <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                            {log.phoneNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            color={log.status === 'completed' ? 'primary' : 
                                   log.status === 'failed' ? 'secondary' : 'default'}
                            size="small"
                            style={{ 
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.templateName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(log.createdOn)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {log.error ? (
                            <Typography 
                              variant="caption" 
                              color="error"
                              style={{ 
                                backgroundColor: '#ffebee', 
                                padding: '4px 8px', 
                                borderRadius: 4,
                                display: 'block',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {log.error}
                            </Typography>
                          ) : (
                            <Chip
                              label={log.messageId ? 'Success' : 'Pending'}
                              color={log.messageId ? 'primary' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {broadcastLogs.length === 0 && (
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center" 
                  p={4}
                  textAlign="center"
                >
                  <Message style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                  <Typography variant="h6" color="textSecondary">
                    No logs found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    No message logs available for this broadcast.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setViewDetailsDialog({ open: false, broadcastId: null })}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Broadcast;
